import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useCan } from '../hooks';
import { ACCOUNT_LABEL, ENGAGEMENT_LABEL, fmtDate, ROLE_LABEL, STAGE_LABEL } from '../format';
import type { Person } from '../types';
import { Avatar, ErrorNote, Icon, Kv, Loading, Modal, Pill, Section, Tabs, toast } from '../components/ui';
import { Change, ConfirmFlow } from '../components/Confirm';
import { ChangeDatesDialog, CreateAccountDialog, OfferDialog, PRE_START, ResendPassDialog, WithdrawDialog } from './Lifecycle';
import { AddDocument, DocTable } from './Documents';
import { DOC_STAGES, DOC_STAGE_LABEL } from '@pti/domain';
import type { DocSummary } from '../types';

const STAGE_TONE: Record<string, string> = { candidate: 'muted', offered: 'warn', withdrawn: 'muted', prestart: 'accent', probation: 'accent', active: 'ok', separating: 'warn', separated: 'muted' };

export function People() {
  const can = useCan();
  const nav = useNavigate();
  const [adding, setAdding] = useState(false);
  const q = useQuery({ queryKey: ['people'], queryFn: () => api.get<Person[]>('/api/people') });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  return (
    <div className="page">
      <div className="page-h">
        <div><p className="eyebrow">Ongoing suitability</p><h1>People</h1><p className="lede">{q.data!.length} people — candidates, staff and contractors.</p></div>
        {can('people:write') && <button className="btn primary" onClick={() => setAdding(true)}>+ Add person</button>}
      </div>
      <div className="table-wrap">
        <table className="tbl click">
          <thead><tr><th>Name</th><th>Position</th><th>Engagement</th><th>Stage</th><th>Microsoft account</th><th>Compliance</th></tr></thead>
          <tbody>
            {q.data!.map((p) => (
              <tr key={p.id} tabIndex={0} onClick={() => nav(`/people/${p.id}`)} onKeyDown={(e) => e.key === 'Enter' && nav(`/people/${p.id}`)}>
                <td><span className="who"><Avatar name={p.name} size={30} /><strong>{p.name}</strong></span></td>
                <td className="wrap">{p.positionTitle ?? (p.roleKey ? ROLE_LABEL[p.roleKey] : '—')}</td>
                <td>{ENGAGEMENT_LABEL[p.engagementType]}{p.fte ? ` · ${p.fte} FTE` : ''}</td>
                <td><Pill tone={STAGE_TONE[p.stage]}>{STAGE_LABEL[p.stage] ?? p.stage}</Pill></td>
                <td><Pill tone={ACCOUNT_LABEL[p.account.status]?.[1]}>{ACCOUNT_LABEL[p.account.status]?.[0] ?? p.account.status}</Pill></td>
                <td>{p.compliance ? <Pill tone={p.compliance.flag ? 'warn' : 'ok'}>{p.compliance.flag ? 'Flagged' : 'Clear'}</Pill> : <span className="dim small">Not linked yet</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adding && <PersonModal onClose={() => setAdding(false)} />}
    </div>
  );
}

const STATES = ['WA', 'NSW', 'VIC', 'QLD', 'SA', 'TAS', 'ACT', 'NT'];
const BASIS_LABEL: Record<string, string> = { hour: 'per hour', year: 'per annum' };
const money = (v: string) => (v ? Number(v).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' }) : '');

export function PersonModal({ onClose, person }: { onClose: () => void; person?: Person }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const can = useCan();
  const pay = can('people:pay');
  const s = (v: string | number | null | undefined) => (v == null ? '' : String(v));
  const initial = {
    givenName: person?.givenName ?? '', familyName: person?.familyName ?? '', personalEmail: person?.personalEmail ?? '', positionTitle: person?.positionTitle ?? '',
    roleKey: person?.roleKey ?? 'trainer', engagementType: person?.engagementType ?? 'casual', fte: s(person?.fte), stage: person?.stage ?? 'candidate',
    phone: s(person?.phone), dateOfBirth: s(person?.dateOfBirth), addressStreet: s(person?.addressStreet), addressSuburb: s(person?.addressSuburb), addressState: person?.addressState ?? 'WA', addressPostcode: s(person?.addressPostcode),
    hoursPerWeek: s(person?.hoursPerWeek), reportsTo: s(person?.reportsTo), workLocation: s(person?.workLocation), award: s(person?.award), classification: s(person?.classification),
    payRate: s(person?.payRate), payBasis: person?.payBasis ?? 'hour',
  };
  const [f, setF] = useState(initial);
  const [section, setSection] = useState<'basics' | 'contact' | 'terms'>('basics');
  const up = (p: Partial<typeof f>) => setF({ ...f, ...p });
  const m = useMutation({
    mutationFn: () => {
      const n = (v: string) => (v.trim() === '' ? null : v.trim());
      const body: Record<string, unknown> = {
        givenName: f.givenName, familyName: f.familyName, personalEmail: n(f.personalEmail), positionTitle: n(f.positionTitle), roleKey: f.roleKey, engagementType: f.engagementType,
        fte: f.fte ? Number(f.fte) : null, stage: f.stage,
        phone: n(f.phone), dateOfBirth: n(f.dateOfBirth), addressStreet: n(f.addressStreet), addressSuburb: n(f.addressSuburb), addressState: f.addressStreet || f.addressSuburb ? f.addressState : null, addressPostcode: n(f.addressPostcode),
        hoursPerWeek: f.hoursPerWeek ? Number(f.hoursPerWeek) : null, reportsTo: n(f.reportsTo), workLocation: n(f.workLocation), award: n(f.award), classification: n(f.classification),
        ...(pay ? { payRate: f.payRate ? Number(f.payRate) : null, payBasis: f.payRate ? f.payBasis : null } : {}),
      };
      return person ? api.send('PATCH', `/api/people/${person.id}`, body) : api.send<{ id: string }>('POST', '/api/people', body);
    },
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['people'] }); void qc.invalidateQueries({ queryKey: ['person'] }); toast(person ? 'Saved' : 'Person added'); onClose(); if (!person && r && typeof r === 'object' && 'id' in r) nav(`/people/${(r as { id: string }).id}`); },
  });
  const txt = (k: keyof typeof f, label: string, props: Record<string, unknown> = {}) => (
    <label className="field" htmlFor={`p-${k}`}><span>{label}</span><input id={`p-${k}`} className="txt" value={f[k]} onChange={(e) => up({ [k]: e.target.value } as Partial<typeof f>)} {...props} /></label>
  );
  const fields = (<>
    <Tabs value={section} onChange={setSection} tabs={[{ id: 'basics', label: 'Basics' }, { id: 'contact', label: 'Contact & address' }, { id: 'terms', label: 'Employment terms' }]} />
    {section === 'basics' && (<>
        <div className="two">{txt('givenName', 'Given name', { required: true })}{txt('familyName', 'Family name', { required: true })}</div>
        <label className="field" htmlFor="p-email"><span>Personal email <span className="dim">(for the account activation pass and signing links)</span></span><input id="p-email" type="email" className="txt" value={f.personalEmail} onChange={(e) => up({ personalEmail: e.target.value })} /></label>
        {txt('positionTitle', 'Position title')}
        <div className="two">
          <label className="field" htmlFor="p-role"><span>Role (picks checklist lists)</span>
            <select id="p-role" value={f.roleKey} onChange={(e) => up({ roleKey: e.target.value })}>{Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </label>
          <label className="field" htmlFor="p-eng"><span>Engagement</span>
            <select id="p-eng" value={f.engagementType} onChange={(e) => up({ engagementType: e.target.value })}>{Object.entries(ENGAGEMENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </label>
        </div>
        <div className="two">
          <label className="field" htmlFor="p-fte"><span>FTE</span><input id="p-fte" type="number" step="0.1" min="0" max="1" className="txt" value={f.fte} onChange={(e) => up({ fte: e.target.value })} /></label>
          <label className="field" htmlFor="p-stage"><span>Stage</span>
            <select id="p-stage" value={f.stage} onChange={(e) => up({ stage: e.target.value })}>{Object.entries(STAGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </label>
        </div>
    </>)}
    {section === 'contact' && (<>
        <div className="two">{txt('phone', 'Phone', { type: 'tel' })}<label className="field" htmlFor="p-dob"><span>Date of birth</span><input id="p-dob" type="date" className="txt" value={f.dateOfBirth} onChange={(e) => up({ dateOfBirth: e.target.value })} /></label></div>
        {txt('addressStreet', 'Street address')}
        <div className="three">
          {txt('addressSuburb', 'Suburb')}
          <label className="field" htmlFor="p-state"><span>State</span><select id="p-state" value={f.addressState} onChange={(e) => up({ addressState: e.target.value })}>{STATES.map((x) => <option key={x}>{x}</option>)}</select></label>
          {txt('addressPostcode', 'Postcode', { inputMode: 'numeric', maxLength: 4 })}
        </div>
        <p className="small dim">Used to fill contracts and letters. Not shared outside PTI People.</p>
    </>)}
    {section === 'terms' && (<>
        <div className="two">{txt('hoursPerWeek', 'Hours per week', { type: 'number', step: '0.5', min: '0', max: '80' })}{txt('reportsTo', 'Reports to')}</div>
        {txt('workLocation', 'Work location')}
        <div className="two">{txt('award', 'Award or agreement')}{txt('classification', 'Classification')}</div>
        {pay ? (
          <div className="two">
            {txt('payRate', 'Pay rate ($)', { type: 'number', step: '0.01', min: '0' })}
            <label className="field" htmlFor="p-basis"><span>Pay basis</span><select id="p-basis" value={f.payBasis} onChange={(e) => up({ payBasis: e.target.value })}><option value="hour">Per hour</option><option value="year">Per annum</option></select></label>
          </div>
        ) : <p className="small dim">Pay is recorded by the Director or RTO Manager.</p>}
    </>)}
  </>);
  if (person) {
    const names: Record<string, string> = {
      givenName: 'Given name', familyName: 'Family name', personalEmail: 'Personal email', positionTitle: 'Position title', roleKey: 'Role', engagementType: 'Engagement', fte: 'FTE', stage: 'Stage',
      phone: 'Phone', dateOfBirth: 'Date of birth', addressStreet: 'Street address', addressSuburb: 'Suburb', addressState: 'State', addressPostcode: 'Postcode',
      hoursPerWeek: 'Hours per week', reportsTo: 'Reports to', workLocation: 'Work location', award: 'Award or agreement', classification: 'Classification', payRate: 'Pay rate', payBasis: 'Pay basis',
    };
    const lbl = (k: string, v: string) => (k === 'roleKey' ? ROLE_LABEL[v] ?? v : k === 'engagementType' ? ENGAGEMENT_LABEL[v] ?? v : k === 'stage' ? STAGE_LABEL[v] ?? v : k === 'dateOfBirth' ? fmtDate(v || null) : k === 'payRate' ? money(v) : k === 'payBasis' ? BASIS_LABEL[v] ?? v : v) || '—';
    const changed = Object.keys(names).filter((k) => (f as Record<string, string>)[k] !== (initial as Record<string, string>)[k])
      .filter((k) => !(k === 'payBasis' && !f.payRate && !initial.payRate)).filter((k) => !(k === 'addressState' && !f.addressStreet && !f.addressSuburb && !initial.addressStreet));
    return (
      <ConfirmFlow
        title={`Edit ${person.name}`}
        onClose={onClose}
        canContinue={changed.length > 0 && !!f.givenName.trim() && !!f.familyName.trim()}
        review={() => (
          <>
            <p>You are about to change {person.name}’s details:</p>
            <ul className="plain-list">{changed.map((k) => <Change key={k} label={names[k]!} from={lbl(k, (initial as Record<string, string>)[k]!)} to={lbl(k, (f as Record<string, string>)[k]!)} />)}</ul>
            {changed.includes('roleKey') && <p className="small">Changing the role does not change their Microsoft 365 group membership or an onboarding checklist already running — update those separately.</p>}
            {changed.includes('personalEmail') && <p className="small">Future activation passes and signing links go to the new email. Links already sent still go to the old one.</p>}
            <p className="small dim">Documents already made are not changed; new documents use the new details.</p>
          </>
        )}
        confirmLabel="Yes, save the changes"
        onConfirm={() => m.mutateAsync()}
      >
        {fields}
        <p className="small dim">Offer and start dates are changed with “Change dates” on the person’s page.</p>
      </ConfirmFlow>
    );
  }
  return (
    <Modal title="Add a person" onClose={onClose} wide>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        {fields}
        {m.error && <ErrorNote error={m.error} />}
        <div className="row gap"><button className="btn primary" disabled={m.isPending || !f.givenName.trim() || !f.familyName.trim()}>Add person</button><button type="button" className="btn" onClick={onClose}>Cancel</button></div>
      </form>
    </Modal>
  );
}

export function PersonPage() {
  const { id } = useParams();
  const can = useCan();
  const [tab, setTab] = useState<'overview' | 'documents'>('overview');
  const [editing, setEditing] = useState(false);
  const [addingDoc, setAddingDoc] = useState(false);
  const [dialog, setDialog] = useState<null | 'offer' | 'dates' | 'withdraw' | 'create' | 'resend'>(null);
  const q = useQuery({ queryKey: ['person', id], queryFn: () => api.get<Person>(`/api/people/${id}`) });
  const docs = useQuery({ queryKey: ['documents', id], queryFn: () => api.get<DocSummary[]>(`/api/documents?personId=${id}`), enabled: tab === 'documents' });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const p = q.data!;
  const acct = ACCOUNT_LABEL[p.account.status];
  return (
    <div className="page">
      <Link className="link back" to="/people"><Icon n="back" s={15} /> People</Link>
      <div className="profile-h">
        <Avatar name={p.name} size={56} />
        <div>
          <h1>{p.name}</h1>
          <p className="lede">{p.positionTitle ?? (p.roleKey ? ROLE_LABEL[p.roleKey] : '')}</p>
          <p className="small dim">{ENGAGEMENT_LABEL[p.engagementType]}{p.fte ? ` · ${p.fte} FTE` : ''} · {STAGE_LABEL[p.stage]}{p.startDate ? ` · Starts ${fmtDate(p.startDate)}` : ''}{p.endDate ? ` · Last day ${fmtDate(p.endDate)}` : ''}</p>
        </div>
        <div className="row gap-s wrap" style={{ marginLeft: 'auto' }}>
          {can('accounts:lock') && ['candidate', 'offered'].includes(p.stage) && <button className={`btn ${p.stage === 'candidate' ? 'primary' : ''}`} onClick={() => setDialog('offer')}>{p.stage === 'candidate' ? 'Send letter of offer' : 'Send a new letter of offer'}</button>}
          {can('checklists:write') && PRE_START.includes(p.stage) && <button className="btn" onClick={() => setDialog('dates')}>Change dates</button>}
          {can('people:write') && <button className="btn" onClick={() => setEditing(true)}>Edit</button>}
          {can('people:write') && PRE_START.includes(p.stage) && <button className="btn danger" onClick={() => setDialog('withdraw')}>Withdraw</button>}
        </div>
      </div>
      <Tabs value={tab} onChange={setTab} tabs={[{ id: 'overview', label: 'Overview' }, { id: 'documents', label: 'Documents' }]} />
      {tab === 'overview' && (
        <div className="grid-2">
          <Section title="Engagement">
            <Kv items={[
              ['Role', p.roleKey ? ROLE_LABEL[p.roleKey] : '—'], ['Personal email', p.personalEmail ?? '—'],
              ['Offer accepted', fmtDate(p.offerAcceptedDate)], ['Start date', fmtDate(p.startDate)], ['Last day', fmtDate(p.endDate)],
            ]} />
            <h4>Contact</h4>
            <Kv items={[
              ['Phone', p.phone ?? '—'],
              ['Address', [p.addressStreet, [p.addressSuburb, p.addressState, p.addressPostcode].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'],
              ['Date of birth', fmtDate(p.dateOfBirth ?? null)],
            ]} />
            <h4>Employment terms</h4>
            <Kv items={[
              ['Hours per week', p.hoursPerWeek ?? '—'], ['Reports to', p.reportsTo ?? '—'], ['Work location', p.workLocation ?? '—'],
              ['Award', p.award ? `${p.award}${p.classification ? ` · ${p.classification}` : ''}` : p.classification ?? '—'],
              ...('payRate' in p ? [['Pay', p.payRate != null ? `${money(String(p.payRate))} ${BASIS_LABEL[p.payBasis ?? ''] ?? ''}` : '—'] as [string, string]] : []),
            ]} />
          </Section>
          <div className="stack">
            <Section title="Microsoft account">
              <Kv items={[['Username', p.account.upn ?? '—'], ['Status', <Pill key="s" tone={acct?.[1]}>{acct?.[0] ?? p.account.status}</Pill>], ['Note', p.account.note ?? '—']]} />
              {can('accounts:lock') && p.stage !== 'withdrawn' && (
                <div className="row gap-s">
                  {['none', 'provisioning'].includes(p.account.status) && PRE_START.includes(p.stage) && p.stage !== 'candidate' && <button className="btn sm" onClick={() => setDialog('create')}>{p.account.status === 'none' ? 'Create account now' : 'Finish set-up'}</button>}
                  {['created', 'active'].includes(p.account.status) && PRE_START.includes(p.stage) && <button className="btn sm" onClick={() => setDialog('resend')}>Send a new sign-in pass</button>}
                </div>
              )}
            </Section>
            <Section title="Checklists">
              {p.runs?.length ? <ul className="plain-list">{p.runs.map((r) => <li key={r.id}><Link className="link" to={`/checklists/${r.id}`}>{r.kind === 'onboarding' ? 'Onboarding' : 'Offboarding'} — CL-{String(r.id).padStart(4, '0')}</Link><span className="small dim">{r.status}</span></li>)}</ul> : <p className="empty">No checklists yet.</p>}
              {can('checklists:write') && <Link className="link" to={`/checklists?start=${p.id}`}>+ Start a checklist</Link>}
            </Section>
            <Section title="Compliance (read only)">
              {p.compliance ? <p><Pill tone={p.compliance.flag ? 'warn' : 'ok'}>{p.compliance.flag ? 'Flagged' : 'Clear'}</Pill> <span className="small">{p.compliance.reason}</span></p> : <p className="small dim">Link to the compliance system and PowerPro not configured yet.</p>}
            </Section>
          </div>
        </div>
      )}
      {tab === 'documents' && (docs.isLoading ? <Loading /> : (
        <div className="stack">
          {can('documents:write') && <div className="row"><button className="btn primary" onClick={() => setAddingDoc(true)}>+ Add a document</button></div>}
          {docs.data?.length ? [...DOC_STAGES, { key: '', label: 'Other' }].map((g) => {
            const list = (docs.data ?? []).filter((d) => (DOC_STAGE_LABEL[d.stage ?? ''] ? d.stage : '') === g.key);
            return list.length ? <Section key={g.key || 'other'} title={g.label}><DocTable docs={list} /></Section> : null;
          }) : <p className="empty">No documents yet.</p>}
        </div>
      ))}
      {addingDoc && <AddDocument personId={p.id} onClose={() => setAddingDoc(false)} />}
      {editing && <PersonModal person={p} onClose={() => setEditing(false)} />}
      {dialog === 'offer' && <OfferDialog person={p} onClose={() => setDialog(null)} />}
      {dialog === 'dates' && <ChangeDatesDialog person={p} onClose={() => setDialog(null)} />}
      {dialog === 'withdraw' && <WithdrawDialog person={p} onClose={() => setDialog(null)} />}
      {dialog === 'create' && <CreateAccountDialog person={p} finish={p.account.status === 'provisioning'} onClose={() => setDialog(null)} />}
      {dialog === 'resend' && <ResendPassDialog person={p} onClose={() => setDialog(null)} />}
    </div>
  );
}
